import React, {useMemo, useEffect} from 'react'
import {
  COMPANY_OWNERSHIP_STRUCTURE,
  SelectPureWithInputContainer,
  useCountrySpecificTranslationKey,
  COMPANY_REVENUE_ANNUAL,
  COMPANY_REVENUE_EXPORTS,
  COMPANY_REVENUE_GROUP,
} from '@modifi/plugin-onboarding'
import {Row, withInputContainer} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {OwnershipStructure} from '@modifi/enums'
import {useInputValue} from '@modifi/plugin-forms'

import {companyOwnershipStructureOptions} from '../constants'
import OwnershipTypeInput from '../components/ownership-type-input'
import {FormContentWrapper} from '../../styles'
import {FormInputComponentCol} from '../../../../../plugins/onboarding'

const OwnershipTypeInputWithInputContainer = withInputContainer(OwnershipTypeInput)

const revenueExportOptions: {label: string; value: number}[] = []
for (let i = 10; i <= 100; i += 10) {
  revenueExportOptions.push({label: `${i}%`, value: i})
}

export type CompanyFinancialsFormContentProps = {
  revenueCategoryOptions?: {
    code?: string
    currency?: string
    max?: number
    min?: number
  }[]
  fetchRevenueCategories: () => void
}

const CompanyFinancialsFormContent: React.FC<CompanyFinancialsFormContentProps> = ({
  revenueCategoryOptions = [],
  fetchRevenueCategories,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])

  useEffect(() => {
    fetchRevenueCategories()
  }, [fetchRevenueCategories])

  const countrySpecificKey = useCountrySpecificTranslationKey()

  const translatedCompanyOwnershipStructureOptions = useMemo(
    () =>
      companyOwnershipStructureOptions.map(
        (option: {label: string | string[]; value: string; description: string}) => ({
          ...option,
          label: t(countrySpecificKey(option.label)),
          description: t(option?.description),
        })
      ),
    [t, countrySpecificKey]
  )

  const {value: companyRevenueAnnualValue} = useInputValue({
    name: COMPANY_REVENUE_ANNUAL,
  })

  const {value: companyRevenueExportsValue} = useInputValue({
    name: COMPANY_REVENUE_EXPORTS,
  })
  const {value: companyOwnerShipValue} = useInputValue({
    name: COMPANY_OWNERSHIP_STRUCTURE,
  })

  const translatedrevenueCategoryOptions = useMemo(
    () =>
      revenueCategoryOptions?.map(option => ({
        label: `${t(`revenue-category.option.${option?.code}`)} ${option?.currency}`,
        value: option?.code || '',
      })),
    [t, revenueCategoryOptions]
  )

  return (
    <FormContentWrapper>
      {!!revenueCategoryOptions?.length && (
        <Row>
          <FormInputComponentCol
            t={t}
            inputName={COMPANY_REVENUE_ANNUAL}
            inputComponent={SelectPureWithInputContainer}
            options={translatedrevenueCategoryOptions}
            inputProps={{
              label: t('intro-page.company-annual-revenue.label'),
              required: true,
            }}
            colProps={{md: 10, lg: 8}}
          />
        </Row>
      )}
      {companyRevenueAnnualValue && !!revenueExportOptions?.length && (
        <Row>
          <FormInputComponentCol
            t={t}
            inputName={COMPANY_REVENUE_EXPORTS}
            inputComponent={SelectPureWithInputContainer}
            options={revenueExportOptions}
            inputProps={{
              label: t('intro-page.company-revenue-exports.label'),
              required: true,
            }}
            colProps={{md: 10, lg: 8}}
          />
        </Row>
      )}
      {companyRevenueExportsValue && (
        <Row mb={8}>
          <FormInputComponentCol
            t={t}
            inputName={COMPANY_OWNERSHIP_STRUCTURE}
            inputComponent={OwnershipTypeInputWithInputContainer}
            options={translatedCompanyOwnershipStructureOptions}
            inputProps={{
              required: true,
            }}
            colProps={{md: 12}}
          />
        </Row>
      )}
      {companyRevenueExportsValue &&
        companyOwnerShipValue === OwnershipStructure.GROUP &&
        !!revenueExportOptions?.length && (
          <Row>
            <FormInputComponentCol
              t={t}
              inputName={COMPANY_REVENUE_GROUP}
              inputComponent={SelectPureWithInputContainer}
              options={translatedrevenueCategoryOptions}
              inputProps={{
                label: t('intro-page.company-revenue-group.label'),
                required: true,
              }}
              colProps={{md: 10, lg: 8}}
            />
          </Row>
        )}
    </FormContentWrapper>
  )
}

export default CompanyFinancialsFormContent
